import type { ZoneDTO } from "../api/client";
import type {
  ProvinceCreateInput,
  ProvinceUpdateInput,
} from "@/modules/geo/domain/contracts";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  Checkbox,
  FormInput,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { useCallback } from "react";
import { z } from "zod";

const ZONE_PAGE_SIZE = 20;

const ProvinceFormSchema = z.object({
  nameLo: z.string().min(1, "ຕ້ອງໃສ່ຊື່ (ລາວ)"),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  zoneId: z.string().nullable().optional(),
  enabled: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

export type ProvinceFormValues = z.output<typeof ProvinceFormSchema>;

const DEV_FILL: ProvinceFormValues = {
  nameLo: "ວຽງຈັນ",
  nameTh: "เวียงจันทน์",
  nameEn: "Vientiane",
  zoneId: null,
  enabled: true,
  sortOrder: 0,
};

type ProvinceFormProps = {
  zones: ZoneDTO[];
  initialValues?: Partial<ProvinceFormValues>;
  onSubmit: (values: ProvinceCreateInput | ProvinceUpdateInput) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function ProvinceForm({
  zones,
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: ProvinceFormProps) {
  const methods = RHF.useForm<ProvinceFormValues>({
    resolver: zodResolver(ProvinceFormSchema) as Resolver<ProvinceFormValues>,
    defaultValues: {
      nameLo: "",
      nameTh: "",
      nameEn: "",
      zoneId: null,
      enabled: true,
      sortOrder: 0,
      ...initialValues,
    },
  });

  const fillDev = () => {
    methods.reset(DEV_FILL);
  };

  const zoneQueryFn = useCallback(
    async ({
      search,
      pageParam = 1,
    }: {
      search: string;
      pageParam: number;
    }) => {
      const s = search.trim().toLowerCase();
      const filtered = s
        ? zones.filter((z) => {
            const code = z.code.toLowerCase();
            const name =
              (z.name as { lo?: string; th?: string; en?: string })?.lo ??
              (z.name as { en?: string })?.en ??
              "";
            return (
              code.includes(s) || name.toLowerCase().includes(s)
            );
          })
        : [...zones];
      const page = pageParam > 0 ? pageParam : 1;
      const start = (page - 1) * ZONE_PAGE_SIZE;
      const items = filtered.slice(start, start + ZONE_PAGE_SIZE);
      const nextPage =
        start + ZONE_PAGE_SIZE < filtered.length ? page + 1 : null;
      return { items, nextPage };
    },
    [zones],
  );

  const zonePreloadFn = useCallback(
    (id: string) => {
      const z = zones.find((x) => x.id === id);
      return Promise.resolve(z ?? null);
    },
    [zones],
  );

  return (
    <FormRoot<ProvinceFormValues>
      methods={methods as UseFormReturn<ProvinceFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
          zoneId: vals.zoneId ?? undefined,
          enabled: vals.enabled,
          sortOrder: vals.sortOrder,
        })
      }
    >
      <div className="space-y-4" data-tourid="geo-province-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">ຂໍ້ມູນແຂວງ</h3>
          {onDevFill && (
            <Button type="button" variant="outline" size="sm" onClick={fillDev}>
              ເຕີມຂໍ້ມູນທົດສອບ
            </Button>
          )}
        </div>
        <FormInput
          name="nameLo"
          label="ຊື່ (ລາວ)"
          placeholder="ພາສາລາວ"
          data-tourid="geo-form-nameLo"
        />
        <FormInput name="nameTh" label="ຊື່ (ไทย)" placeholder="ภาษาไทย" />
        <FormInput name="nameEn" label="ຊື່ (EN)" placeholder="English" />
        <div data-tourid="geo-form-zone">
          <label className="mb-1 block font-medium text-sm">ໂຊນ</label>
          <RHF.Controller
            name="zoneId"
            control={methods.control}
            render={({ field }) => (
              <InfiniteCombobox<ZoneDTO>
                queryKey={["geo", "zones"]}
                queryFn={zoneQueryFn}
                preloadQueryFn={zonePreloadFn}
                getLabel={(item) => item.code}
                getValue={(item) => item.id}
                value={(field.value as string | null) ?? ""}
                onValueChange={(v) => field.onChange(v === "" ? null : v)}
                placeholder="— ບໍ່ເລືອກ"
                clearable
              />
            )}
          />
        </div>
        <div className="flex items-center gap-2" data-tourid="geo-form-enabled">
          <RHF.Controller
            name="enabled"
            control={methods.control}
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <label className="font-medium text-sm">ເປີດສະແດງໃນ dropdown</label>
        </div>
        <FormInput name="sortOrder" label="ລຳດັບ" type="number" min={0} />
        <div className="flex gap-2 pt-2" data-tourid="geo-form-submit">
          <Button type="submit" disabled={submitting}>
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset())}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
